import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { CampaignStockVinsService } from '../../../../../core/services/campaign-stock-vins/campaign-stock-vins.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-bastidores-import',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './bastidores-import.component.html',
  styleUrl: './bastidores-import.component.css'
})
export class BastidoresImportComponent {
  @Input() campaignStockId: string = '';
  @Output() importSuccess = new EventEmitter<any>();

  isDragOver = false;

  constructor(
    private alertService: AlertService,
    private campaignStockVinsService: CampaignStockVinsService,
    private translate: TranslateService
  ) { }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFileUpload(file);
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const droppedFile = event.dataTransfer?.files?.[0];
    if (droppedFile) {
      this.handleFileUpload(droppedFile);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  private validateExcelFile(file: File): { valid: boolean; error?: string } {
    const allowedExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return { valid: false, error: 'swal_messages.invalid_file_extension_excel' };
    }

    const allowedMimeTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/csv'
    ];

    if (file.type && !allowedMimeTypes.includes(file.type)) {
      return { valid: false, error: 'swal_messages.invalid_file_type_excel' };
    }

    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return { valid: false, error: 'swal_messages.file_too_large_10mb' };
    }

    if (file.size === 0) {
      return { valid: false, error: 'swal_messages.file_is_empty' };
    }

    return { valid: true };
  }

  handleFileUpload(file: File): void {
    const validation = this.validateExcelFile(file);

    if (!validation.valid) {
      this.alertService.error(validation.error || 'swal_messages.invalid_file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('campaign_stock_id', this.campaignStockId);

    this.alertService.loading('swal_messages.please_wait');

    this.campaignStockVinsService.importVins(formData).subscribe({
      next: (response) => {
        this.alertService.close();
        const summary = response.data?.summary;
        if (summary) {
          const errorCount = summary.errors?.length || 0;
          const parts = [
            `${this.translate.instant('bastidores.import_created')}: ${summary.created || 0}`,
            `${this.translate.instant('bastidores.import_deleted')}: ${summary.deleted || 0}`,
            `${this.translate.instant('bastidores.import_skipped')}: ${summary.skipped || 0}`,
            `${this.translate.instant('bastidores.import_errors')}: ${errorCount}`,
          ];
          const summaryLine = parts.join(', ');

          let warnings = '';

          // Errores por fila
          const errors: any[] = summary.errors ?? [];
          if (errors.length > 0) {
            const rowLabel = this.translate.instant('bastidores.import_unresolved_row');
            const errorRows = errors
              .map((e: any) => `<li>${rowLabel} ${e.row}: ${this.translate.instant('bastidores.import_reason_' + e.reason)}</li>`)
              .join('');
            warnings += `<hr><p style="color:#dc3545;margin-bottom:4px"><strong>${this.translate.instant('bastidores.import_errors')}</strong></p>
              <ul style="text-align:left;font-size:0.85em">${errorRows}</ul>`;
          }

          // Talleres no resueltos
          const unresolvedWorkshops: any[] = summary.unresolved_workshops ?? [];
          if (unresolvedWorkshops.length > 0) {
            const rowLabel = this.translate.instant('bastidores.import_unresolved_row');
            const workshopRows = unresolvedWorkshops
              .map((w: any) => `<li>${rowLabel} ${w.row}: <strong>${w.workshop_name}</strong></li>`)
              .join('');
            warnings += `<hr><p style="color:#856404;margin-bottom:4px"><strong>⚠ ${this.translate.instant('bastidores.import_unresolved_workshops')}</strong></p>
              <p style="font-size:0.9em">${this.translate.instant('bastidores.import_unresolved_workshops_desc')}</p>
              <ul style="text-align:left;font-size:0.85em">${workshopRows}</ul>`;
          }

          // Concesionarios no resueltos
          const unresolvedDealerships: any[] = summary.unresolved_dealerships ?? [];
          if (unresolvedDealerships.length > 0) {
            const rowLabel = this.translate.instant('bastidores.import_unresolved_row');
            const dealershipRows = unresolvedDealerships
              .map((d: any) => `<li>${rowLabel} ${d.row}: <strong>${d.dealership_name}</strong></li>`)
              .join('');
            warnings += `<hr><p style="color:#856404;margin-bottom:4px"><strong>⚠ ${this.translate.instant('bastidores.import_unresolved_dealerships')}</strong></p>
              <p style="font-size:0.9em">${this.translate.instant('bastidores.import_unresolved_dealerships_desc')}</p>
              <ul style="text-align:left;font-size:0.85em">${dealershipRows}</ul>`;
          }

          if (warnings) {
            Swal.fire({
              title: this.translate.instant('swal_messages.success'),
              html: `<p>${summaryLine}</p>${warnings}`,
              icon: 'warning',
              confirmButtonColor: '#3085d6',
              confirmButtonText: 'OK',
            });
          } else {
            this.alertService.successOk(summaryLine);
          }
        } else {
          this.alertService.successOk('swal_messages.campaign_stock_vins.campaign_stock_vins_imported');
        }
        this.importSuccess.emit(response.data);
      },
      error: (error) => {
        this.alertService.error(error.error);
      },
    });
  }
}
